import Phaser from "phaser";
import type { BigNumber } from "@yjsgames/bignumber";
import type { ProjectileElement, ProjectileSpec } from "../models/ProjectileSpec.js";
import { computeProjectileDamage } from "../models/ProjectileSpec.js";
import { COMBAT } from "../data/balance.js";
import {
  fallsBelowFloor,
  reachesWall,
  reflectInBounds,
  reflectVelocityOffRect,
  type PlayfieldBounds,
} from "./bounceBounds.js";
import { computeHomingVelocity, DEFAULT_HOMING_TURN_RATE } from "./homingTrack.js";
import { DEPTH } from "./depthLayers.js";
import { PALETTE_NUM } from "../themes/palette.js";
import { shouldSpawnGhost } from "./projectileTrail.js";
import { projectileHeadingRad } from "./projectileHeading.js";

export const DEFAULT_PROJECTILE_TEXTURE_KEY = "projectile_basic_white";

/** 가장 가까운 활성 벽돌 좌표 조회 (homing 조향용). 대상 없으면 null. */
export type NearestBrickLocator = (x: number, y: number) => { x: number; y: number } | null;

/** 잔상 고스트 1개 방출 — 발사체 위치/색/회전을 받아 셸(BattlefieldRunner)이 고스트 풀로 띄운다. */
export type GhostEmitter = (x: number, y: number, tint: number, rotation: number) => void;

/** 발사체 소멸(벽/안전망/화면이탈) 통지 — 셸이 풀로 반납한다(destroy 아님). */
export type ProjectileDespawn = (projectile: Projectile) => void;

/** element별 발사체 색상 — 모듈 변경이 즉시 화면에 드러나게 하는 시각 차별화의 축 (성공 기준 §30). */
const ELEMENT_COLOR: Record<ProjectileElement, number> = {
  none: PALETTE_NUM.projectileNeutral,
  fire: PALETTE_NUM.projectileFire,
  electric: PALETTE_NUM.projectileElectric,
  ice: PALETTE_NUM.projectileIce,
};

const HOMING_TARGET_SAMPLE_MS = 80;

/**
 * 발사체 — Arcade 물리 스프라이트("ball" 텍스처 tint). spec을 들고 다니며 behavior(이동)·소멸(반사 횟수 소진→벽 접촉, 화면이탈, 하드캡 안전망)을 자체 처리.
 * 피해 적용/impact(폭발·연쇄)/관통 차감은 GameScene 충돌 핸들러가 담당한다 (벽돌 그룹 접근 필요).
 */
export class Projectile extends Phaser.Physics.Arcade.Sprite {
  // ── per-spawn 상태(풀 재사용마다 configure가 전부 갱신) — readonly 아님: 같은 인스턴스를 재사용하므로 ──
  spec!: ProjectileSpec;
  damage!: BigNumber;
  pierceLeft = 0;
  /** 남은 벽 반사 횟수 = 반사(bounce) 모듈 수. 0이면 벽에 닿을 때 반사 없이 소멸. */
  bounceLeft = 0;
  /** 크리티컬 발사체 여부 — ProjectileFactory.spawn이 rollCrit 결과로 set. 충돌 시 critical! 표시·폭발 색 분기에 사용. */
  isCrit = false;
  /** 분열 공이 첫 블록 충돌에서 반사를 이미 썼는지 — 분열 발동+반사는 첫 충돌 1회만(이후 일반 소멸/관통). bounceLeft와 독립. */
  splitBounceUsed = false;
  /** 관통 시 같은 벽돌 중복 타격 방지 — overlap 콜백은 매 프레임 호출되므로 이미 맞힌 대상을 기록. 인스턴스 재사용 시 .clear()만. */
  readonly hitSet = new Set<Phaser.GameObjects.GameObject>();
  private createdAtMs = 0;
  private locator!: NearestBrickLocator;
  /** element 색 — 잔상 고스트가 같은 색으로 페이드하도록 보관. */
  private elementTint = 0;
  private lastGhostAt = 0;
  /** 벽 반사 경계 (게임뷰 프레임 interior) — configure에서 주입. SSoT는 GameScene.playfieldBounds. */
  private bounds!: PlayfieldBounds;
  /** 잔상 고스트 방출 — 셸(BattlefieldRunner)이 고스트 풀로 띄운다. configure에서 주입. */
  private emitGhost?: GhostEmitter;
  /** 소멸(벽/안전망/화면이탈) 통지 — 셸이 풀로 반납. configure에서 주입. */
  private despawn?: ProjectileDespawn;
  /** 직전 sweep 검사 시점 위치 — 연속 충돌(터널링 방지)용. BattlefieldRunner.update가 매 프레임 prev→현재 선분으로 검사 후 commit. */
  private sweepPrevX = 0;
  private sweepPrevY = 0;
  private lastHomingTargetAt = Number.NEGATIVE_INFINITY;
  private homingTarget: { x: number; y: number } | null = null;

  /**
   * 1회 초기화만 — 텍스처/물리 바디/형상/blend/depth는 재사용 내내 유지된다(풀링 핵심). per-spawn 상태는
   * configure가 매 acquire마다 전부 리셋한다. 생성 직후엔 비활성(configure 전까지 미렌더·미업데이트).
   */
  constructor(scene: Phaser.Scene) {
    super(scene, 0, 0, DEFAULT_PROJECTILE_TEXTURE_KEY);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setCircle(COMBAT.ballRadius);
    body.setAllowGravity(false);
    // 발광 구체(가독: 탄환 강조) — 다크 배경 위 ADD 블렌드로 자체 발광감.
    this.setBlendMode(Phaser.BlendModes.ADD);
    this.setDepth(DEPTH.projectile);
    this.setActive(false).setVisible(false);
  }

  /**
   * 발사체를 1회 발사분으로 재구성(신규/풀 재사용 공통) — 모든 per-spawn 상태를 리셋한다.
   * 누락하면 재사용 시 stale 상태가 남는다: createdAtMs(즉시 safetyCap 소멸)·scale(crit 1.4/split 0.55 잔존)·
   * sweepPrev(첫 프레임 거대 선분 오탐)·잔여 fade tween. caller(factory/spawnSplit)는 직후 isCrit/scale을 set.
   */
  configure(
    x: number,
    y: number,
    spec: ProjectileSpec,
    locator: NearestBrickLocator,
    bounds: PlayfieldBounds,
    emitGhost: GhostEmitter,
    despawn: ProjectileDespawn,
    textureKey = DEFAULT_PROJECTILE_TEXTURE_KEY,
  ): void {
    this.scene.tweens.killTweensOf(this); // 이전 수명의 fade tween 잔재 제거(재사용 race 방어)
    this.spec = spec;
    this.damage = computeProjectileDamage(spec);
    this.pierceLeft = spec.pierceCount;
    this.bounceLeft = spec.bounceCount;
    this.isCrit = false;
    this.splitBounceUsed = false;
    this.hitSet.clear();
    this.createdAtMs = this.scene.time.now;
    this.locator = locator;
    this.bounds = bounds;
    this.emitGhost = emitGhost;
    this.despawn = despawn;
    this.elementTint = ELEMENT_COLOR[spec.element];
    this.lastGhostAt = 0;
    this.lastHomingTargetAt = Number.NEGATIVE_INFINITY;
    this.homingTarget = null;
    this.sweepPrevX = x;
    this.sweepPrevY = y;
    const managed = this as Phaser.GameObjects.GameObject & {
      displayList?: unknown;
      addToDisplayList?: () => unknown;
    };
    if (!managed.displayList) managed.addToDisplayList?.();
    // 바디 재활성 + (x,y) 리셋 + active/visible 복구 (velocity 0 — launch가 직후 set).
    this.enableBody(true, x, y, true, true);
    if (this.texture.key !== textureKey) this.setTexture(textureKey);
    this.setScale(1).setAlpha(1);
    if (spec.element === "none" && textureKey !== DEFAULT_PROJECTILE_TEXTURE_KEY) {
      this.clearTint();
    } else {
      this.setTint(this.elementTint);
    }
    this.setContentMask(undefined); // caller가 게임뷰 마스크 재적용
  }

  /** 소멸 처리 — 비활성+숨김+바디 off. 풀 반납(onRelease)이 호출. 잔여 tween도 정리. */
  deactivate(): void {
    this.scene.tweens.killTweensOf(this);
    this.disableBody(true, true); // body.stop + enable=false + setActive(false) + setVisible(false)
    (this as Phaser.GameObjects.GameObject & { removeFromDisplayList?: () => unknown }).removeFromDisplayList?.();
  }

  launch(vx: number, vy: number): void {
    (this.body as Phaser.Physics.Arcade.Body).setVelocity(vx, vy);
    this.sweepPrevX = this.x;
    this.sweepPrevY = this.y;
    this.syncHeading(vx, vy);
  }

  /** 직전 sweep 검사 시점 위치 (연속 충돌 선분의 시작점). */
  sweepPrev(): { x: number; y: number } {
    return { x: this.sweepPrevX, y: this.sweepPrevY };
  }

  /** sweep 검사 후 현재 위치를 prev로 커밋 — 다음 프레임 선분의 시작점이 된다. */
  commitSweepPos(): void {
    this.sweepPrevX = this.x;
    this.sweepPrevY = this.y;
  }

  /** 현재 속도(body 없으면 0,0). 외부가 Arcade.Body를 직접 들추지 않도록 캡슐화한 접근자. */
  currentVelocity(): { x: number; y: number } {
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    return { x: body?.velocity.x ?? 0, y: body?.velocity.y ?? 0 };
  }

  /**
   * 벽돌 면에서 튕겨낸다 — 닿은 면(좌우/상하)의 법선 축만 반전(reflectVelocityOffRect), 횡 성분은 보존.
   * 분열 공의 첫 충돌 반사용(벽 반사 bounceLeft와 독립). 속도 정반대(180도 역행)가 아니라 자연스러운 반사다.
   * hitSet을 비워 반사 후 같은 블록을 다시 칠 수 있게 한다.
   */
  reflectOffBrick(brickX: number, brickY: number, halfW: number, halfH: number): void {
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    if (!body) return;
    const v = reflectVelocityOffRect(
      body.velocity.x,
      body.velocity.y,
      this.x,
      this.y,
      brickX,
      brickY,
      halfW,
      halfH,
    );
    body.velocity.set(v.vx, v.vy);
    this.syncHeading(v.vx, v.vy);
    this.hitSet.clear();
  }

  override preUpdate(time: number, delta: number): void {
    super.preUpdate(time, delta);
    const body = this.body as Phaser.Physics.Arcade.Body | null;
    if (!body) return;

    // 잔상 트레일(가독: 탄도 시인성) — 스로틀된 고스트가 짧게 페이드 (발사체당 동시 ~3개). 셸이 고스트 풀로 띄운다.
    if (shouldSpawnGhost(time, this.lastGhostAt)) {
      this.lastGhostAt = time;
      this.emitGhost?.(this.x, this.y, this.elementTint, this.rotation);
    }

    if (this.spec.behavior === "homing") this.applyHoming(time, body);

    // 벽 상호작용: 반사 횟수(bounceLeft = 반사 모듈 수)가 남으면 프레임 벽에서 튕기고 1 차감,
    // 0이면 좌/우/상 프레임 벽에 닿는 순간 소멸한다 — 반사는 반사(bounce) 모듈이 있어야만 일어난다.
    const r = COMBAT.ballRadius;
    if (this.bounceLeft > 0) {
      // 함수 입력(vx/vy)과 출력을 직접 비교 — body.velocity를 다시 읽지 않아 부동소수 오판 없이 반사 발생 여부 판정.
      const vx = body.velocity.x;
      const vy = body.velocity.y;
      const v = reflectInBounds(this.x, this.y, vx, vy, this.bounds, r);
      if (v.vx !== vx || v.vy !== vy) {
        body.velocity.x = v.vx;
        body.velocity.y = v.vy;
        this.syncHeading(v.vx, v.vy);
        this.bounceLeft -= 1;
        // 반사 = 새 궤적 진입 → 이전에 맞힌 벽돌을 다시 칠 수 있게 중복-방지 기록을 비운다.
        // (관통+반사 조합에서 통과한 벽돌로 되돌아올 때 hitSet 영속이 재타격을 막던 갭 해소.)
        this.hitSet.clear();
      }
    } else if (
      reachesWall(this.x, this.y, this.bounds, r) ||
      fallsBelowFloor(this.y, body.velocity.y, this.bounds, r)
    ) {
      // 반사 모듈이 없으면 좌/우/상 벽에 닿거나, 되돌아 하단으로 떨어질 때 소멸한다(풀로 반납).
      this.despawn?.(this);
      return;
    }

    // 하드캡 안전망: 벽·벽돌에 닿지 않고(속도 0/궤도 순환) 영원히 사는 공을 최후 차단. 정상 공은 위 벽/반사 소멸로 그 전에 사라진다.
    if (time - this.createdAtMs > COMBAT.projectileSafetyCapMs) {
      this.despawn?.(this);
      return;
    }
    // 안전망: 어떤 이유로든 화면 아래로 완전히 벗어난 공 제거 (정상 경로는 위 벽/바닥 처리).
    if (this.y > this.scene.scale.height + 20) this.despawn?.(this);
  }

  /**
   * 게임뷰 콘텐츠 마스크를 발사체 본체에 적용한다(펼침 시 창 밖 삐짐 방지). BattlefieldRunner가 spawn 직후 1회 호출.
   * 잔상 고스트는 셸(고스트 풀)이 같은 게임뷰 마스크를 별도로 적용한다.
   */
  setContentMask(mask: Phaser.Display.Masks.GeometryMask | undefined): void {
    if (mask) this.setMask(mask);
    else this.clearMask();
  }

  // 유도 조향 — 결정 로직은 순수함수 computeHomingVelocity(homingTrack.ts)에 위임해 단위테스트로 회귀 검증한다.
  // (속도 크기 보존 + 가장 가까운 벽돌로 매 프레임 DEFAULT_HOMING_TURN_RATE만큼 선회. 대상 없거나 정지면 그대로.)
  private applyHoming(time: number, body: Phaser.Physics.Arcade.Body): void {
    if (time - this.lastHomingTargetAt >= HOMING_TARGET_SAMPLE_MS) {
      this.lastHomingTargetAt = time;
      this.homingTarget = this.locator(this.x, this.y);
    }
    const v = computeHomingVelocity(
      this.x,
      this.y,
      body.velocity.x,
      body.velocity.y,
      this.homingTarget,
      DEFAULT_HOMING_TURN_RATE,
    );
    if (v) {
      body.velocity.set(v.x, v.y);
      this.syncHeading(v.x, v.y);
    }
  }

  private syncHeading(vx: number, vy: number): void {
    this.setRotation(projectileHeadingRad(vx, vy, this.rotation));
  }
}
